/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { addClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { SetupStep, SetupStepCondition } from 'models/setup/SetupStep';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import {
  copyProjectStepSetupStep,
  findProjectStep,
  findProjectStepType,
} from 'apis/setup/SetupStep';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { Code } from 'models/common/CommonCode';
import SetupStepContent from './SetupStepContent';

interface Props {
  open: boolean;
  close: () => void;
  elmPrjId: string;
  onRefresh: () => void;
}
interface SetupStepCopy extends SetupStep {
  tgtYn?: string;
}
const SetupStepCopyPopup = ({ open, close, onRefresh, ...props }: Props) => {
  const gridRef = useRef<any>();
  const stepRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const defaultCondition = {
    bildPlntCds: [],
    elmPrjId: '',
  };
  const [condition, setCondition] = useState<SetupStepCondition>({ ...defaultCondition });
  const [errors, setErrors] = useState<any>({});
  const [projects, setProjects] = useState<SetupStep[]>([]);
  const [code, setCode] = useState<any>();
  const [prjSpCds, setPrjSpCds] = useState<SetupStep[]>([]);
  const [rowData, setRowData] = useState<SetupStepCopy[]>([]);

  useEffect(() => {
    getCommonCodes();
    findProjectStep({}).then((result) => {
      setProjects(result || []);
    });
  }, []);

  useEffect(() => {
    handleSearch();
  }, []);

  const optionsPrjMaster = useMemo(() => {
    return (projects || [])
      .filter((o) =>
        (condition?.bildPlntCds || []).length > 0
          ? condition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .filter((o) => o.elmPrjId !== props?.elmPrjId)
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [projects, condition?.bildPlntCds]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('FA_SU_PLNT_CD'), // 플랜트
    ]).then(([bildPlntCd]) => {
      setCode({
        bildPlntCd: bildPlntCd,
      });
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handleSearch = () => {
    if (condition?.elmPrjId) {
      stepRef.current?.search({ elmPrjId: condition?.elmPrjId });
      stepRef.current?.resetTabIndex?.();
      findProjectStepType(condition?.elmPrjId).then((result) => {
        setRowData(result);
      });
    }
  };

  const layoutDefinition = useMemo(() => {
    const mapPrjSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    mapPrjSpCd.getDisplayValues = (item) => {
      // 선택한 셋업구분값에 따라 셋업단계 콤보박스 필터링
      return (prjSpCds || [])
        .filter((o) => o.istlDivsCd === item.istlDivsCd)
        .map((o) => o.prjSpNm || '');
    };
    const mapAutoCalSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    mapAutoCalSpCd.getDisplayValues = (item) => {
      // 선택한 셋업구분값에 따라 자동계산단계 콤보박스 필터링 (+선택한 셋업단계값은 제외)
      return (prjSpCds || [])
        .filter(
          (o) => o.istlDivsCd === item.istlDivsCd && item.prjSpCd && o.prjSpCd !== item.prjSpCd
        )
        .map((o) => o.prjSpNm || '');
    };

    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'eqclIds',
        header: String(t('setup.label.설비군', '설비군')),
        minWidth: 80,
        width: '*',
        isRequired: false,
        isReadOnly: true,
        align: 'left',
        cssClass: 'WijmoFind',
        cellTemplate: (params) => `
          <span>${params.value || ''}</span>
          <Button />  
        `,
      },
      {
        binding: 'spTpNm',
        header: String(t('setup.label.단계 유형', '단계 유형')),
        align: 'left',
        minWidth: 40,
        width: '*',
      },
      {
        binding: 'wghtScor',
        header: String(t('setup.label.가중치', '가중치')),
        align: 'center',
        minWidth: 70,
        width: 100,
      },
      {
        binding: 'tgtYn',
        header: String(t('setup.label.대상 선택', '대상 선택')),
        align: 'center',
        minWidth: 70,
        width: 100,
        cssClass: 'WijmoCheck',
        cellTemplate: (params) => {
          const checked = params.value === 'Y' ? 'checked' : '';
          return `<div class="checkbox">
                  <input type="checkbox" id="tgtYn_${params.row.index}" ${checked} />
                  <label />
                </div>
          `;
        },
      },
    ];
  }, [code, prjSpCds]);

  const handleConfirm = () => {
    const rows = (rowData || []).filter((o) => o.tgtYn === 'Y');
    if (rows.length < 1) {
      openMessageBar({
        content: t('setup.label.선택된 항목이 없습니다.', '선택된 항목이 없습니다.'),
      });
      return;
    }
    if (!props?.elmPrjId) {
      openMessageBar({
        content: t('setup.label.프로젝트ID가 없습니다.', '프로젝트ID가 없습니다.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t(
        'setup.label.선택한 항목과 동일하게 단계유형, 공통단계, 셋업단계가 모두 변경되며, 기존 등록된 셋업정보는 삭제됩니다.<br/>복사하시겠습니까?',
        '선택한 항목과 동일하게 단계유형, 공통단계, 셋업단계가 모두 변경되며, 기존 등록된 셋업정보는 삭제됩니다.<br/>복사하시겠습니까?'
      ),
      yesCallback: () => {
        copyProjectStepSetupStep(props?.elmPrjId, rows)
          .then((res) => {
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res.successOrNot === 'Y'
                  ? t('setup.label.복사되었습니다.', '복사되었습니다.')
                  : res.data && typeof res.data === 'string'
                  ? res.data
                  : t('setup.label.복사 중 오류가 발생했습니다.', '복사 중 오류가 발생했습니다.'),
            });
            if (res?.successOrNot === 'Y') {
              onRefresh && onRefresh();
            }
          })
          .finally(() => {
            close();
          });
      },
    });
  };

  const onInitialized = (grid) => {
    grid.autoRowHeights = true;
    gridRef.current = grid;
    grid.formatItem.addHandler((s, e) => {
      const item = s.rows[e.row]?.dataItem;
      if (e.panel === s.columnHeaders) {
        e.cell.style.display = 'flex';
        e.cell.style.alignItems = 'center';
        e.cell.style.justifyContent = 'center';
        const text = e.cell.textContent ?? '';
        if (text.includes('\n')) {
          e.cell.innerHTML = text.replace(/\n/g, '<br/>');
        }
      }
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          if (item?.ivesMdlExistYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return;

      const item = grid.rows[ht.row].dataItem;
      const binding = grid.columns[ht.col].binding;

      if (CellType.Cell === ht.cellType) {
        item.tgtYn = item?.tgtYn !== 'Y' ? 'Y' : 'N';
        gridRef.current?.refresh();
      }
      // 대상 선택
      /*if ('tgtYn' === binding && e.target.id?.startsWith('tgtYn')) {
        item.tgtYn = e.target.checked ? 'Y' : 'N';
        // gridRef.current?.refresh();
      }*/
    });
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={String(t('setup.label.프로젝트 단계 복사', '프로젝트 단계 복사'))}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.플랜트', '플랜트'))}</span>
                </label>
                <MultiComboBox
                  id="bildPlntCds"
                  options={code?.bildPlntCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(rowData || []).filter(
                        (o) =>
                          condition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols style={{ width: '65%' }}>
                <label>
                  <span className="dot">{String(t('setup.label.프로젝트명', '프로젝트명'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={condition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultCondition}
              setReset={setCondition}
              onSearch={() => handleSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <div style={{ marginTop: '12px', marginBottom: '12px' }}>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={190}
          isSelector={false}
          isFilter={false}
          isReadOnly={true}
          allowPinning={false}
          initialized={onInitialized}
        />
      </div>
      <SetupStepContent ref={stepRef} isReadOnly={true} />
    </CustomDialog>
  );
};

export default SetupStepCopyPopup;
